/**
 * ExpandCollapseBlock logic
 * Accordion mode (single panel open) vs. standard mode (multiple panels can be open).
 */

// Add type definitions for UMD with AMD property
declare function define(
  dependencies: string[],
  factory: (...args: any[]) => any
): void;
declare function define(
  factory: (...args: any[]) => any
): void;
declare namespace define {
  const amd: boolean;
}

(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD
    define([], factory);
  } else if (typeof module === 'object' && typeof module.exports === 'object') {
    // CommonJS
    module.exports = factory();
  } else {
    // Browser globals
    root.ExpandCollapse = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {

  interface ToggleContext {
    trigger: HTMLElement;
    parent: HTMLElement;
    triggerText: HTMLElement | undefined;
    moreText: string;
    lessText: string;
    target: HTMLElement | undefined;
    shouldBeVisibleAtStart: boolean;
  }

  const SELECTORS = {
    READ_TOGGLE: '[data-js="read-toggle"]',
    READ_TARGET: '[data-js="read-target"]',
    NO_JS_CLASS: 'erv-no-js',
    TOGGLE_TEXT_CLASS: '.toggle-text',
    CONTROL_PANEL_CLASS: '.control-panel',
    EXPAND_COLLAPSE_CONTAINER: '.expand-collapse-container',
    EXPAND_COLLAPSE_NESTED: '.expand-collapse-nested',
  };

  const CLASSNAMES = {
    NOJS_SHOW: 'nojs-show',
    DEAD: 'dead',
    QUESTION: 'question',
    SHOW_ALL: 'show-all',
    IS_EXPANDED: 'is-expanded',
    SELECTED: 'selected',
    SHOW: 'show',
    COLLAPSE: 'collapse',
  };

  const DATA_ATTRIBUTES = {
    STATE: 'data-state',
    ACCORDION: 'data-accordion',
    IS_VISIBLE_AT_START: 'data-is-visible-at-start',
    MORE_TEXT: 'data-moretext',
    LESS_TEXT: 'data-lesstext',
    PANEL_MORE: 'panelmore',
    PANEL_LESS: 'panelless',
    OVERRIDE_MORE_LESS: 'data-overridemoreless',
    PANEL_MORE_ATTRIBUTE: 'data-panelmore',
    PANEL_LESS_ATTRIBUTE: 'data-panelless',
  };

  const STATES = {
    MORE: 'more',
    LESS: 'less',
  };

  // ---------- CONTEXT CREATION ----------
  function buildToggleContext(trigger: HTMLElement): ToggleContext | undefined {
    const parent = trigger.parentElement;
    if (!parent) return undefined;

    const triggerTextElement = trigger.querySelector<HTMLElement>(SELECTORS.TOGGLE_TEXT_CLASS)!;
    const moreText = trigger.getAttribute(DATA_ATTRIBUTES.MORE_TEXT) || '';
    const lessText = trigger.getAttribute(DATA_ATTRIBUTES.LESS_TEXT) || '';
    const targetElement = getTarget(trigger, parent);

    return {
      trigger,
      parent,
      triggerText: triggerTextElement,
      moreText,
      lessText,
      target: targetElement ?? undefined,
      shouldBeVisibleAtStart: trigger.getAttribute(DATA_ATTRIBUTES.IS_VISIBLE_AT_START) === 'True',
    };
  }

  // ---------- INITIAL VISIBILITY ----------
  function applyInitialVisibility(ctx: ToggleContext): void {
    if (isNoJsModeActive()) return;

    const { trigger, target, shouldBeVisibleAtStart, moreText, lessText, triggerText, parent } = ctx;

    if (!shouldBeVisibleAtStart) {
      hideTarget(target);
      if (triggerText && moreText) {
        triggerText.textContent = moreText;
      }
      updateTriggerState(trigger, STATES.MORE);
      updateControlPanelText(trigger, DATA_ATTRIBUTES.PANEL_MORE);
    } else {
      parent.classList.add(CLASSNAMES.IS_EXPANDED);
      if (triggerText && lessText) {
        triggerText.textContent = lessText;
      }
      updateTriggerState(trigger, STATES.LESS);
      updateControlPanelText(trigger, DATA_ATTRIBUTES.PANEL_LESS);
      revealTarget(target);
    }

    target?.classList.remove(CLASSNAMES.NOJS_SHOW);

    if (isTargetEffectivelyEmpty(target) && !trigger.classList.contains(CLASSNAMES.SHOW_ALL)) {
      markAsDead(trigger);
    }
  }

  // ---------- TOGGLE BEHAVIOR ----------
  function setToggleBehavior(ctx: ToggleContext): void {
    const { trigger, parent, target, moreText, lessText, triggerText } = ctx;

    trigger.addEventListener('click', (evt) => {
      evt.preventDefault();
      if (isDead(trigger)) return;

      if (isQuestion(trigger)) {
        parent.classList.toggle(CLASSNAMES.SELECTED);
      }

      const currentState = getCurrentState(trigger);

      if (currentState === STATES.MORE) {
        updatePanelState(trigger, parent, target, lessText, triggerText, STATES.LESS, DATA_ATTRIBUTES.PANEL_LESS);
        if (isAccordionMode(trigger) && isTopLevelAccordionTrigger(trigger)) {
          closeOtherOpenPanelsInAccordion(trigger);
        }
      } else {
        updatePanelState(trigger, parent, target, moreText, triggerText, STATES.MORE, DATA_ATTRIBUTES.PANEL_MORE);
      }
    });
  }

  // ---------- PANEL STATE UPDATE ----------
  function updatePanelState(
    trigger: HTMLElement,
    parent: HTMLElement,
    target: HTMLElement | undefined,
    newText: string,
    triggerText: HTMLElement | undefined,
    newState: string,
    panelStateDataAttr: string
  ): void {
    if (triggerText && newText) {
      triggerText.textContent = newText;
    }

    updateTriggerState(trigger, newState);
    parent.classList.toggle(CLASSNAMES.IS_EXPANDED, newState === STATES.LESS);
    updateControlPanelText(trigger, panelStateDataAttr);

    newState === STATES.LESS ? revealTarget(target) : hideTarget(target);
  }

  // ---------- ACCORDION MODE HELPERS ----------
  function isAccordionMode(trigger: HTMLElement): boolean {
    const container = trigger.closest(SELECTORS.EXPAND_COLLAPSE_CONTAINER);
    return container?.getAttribute(DATA_ATTRIBUTES.ACCORDION) === 'true';
  }

  function isTopLevelAccordionTrigger(trigger: HTMLElement): boolean {
    return !trigger.closest(SELECTORS.EXPAND_COLLAPSE_NESTED);
  }

  /**
   * Close any other open panels in the same accordion container.
   */
  function closeOtherOpenPanelsInAccordion(currentTrigger: HTMLElement): void {
    const container = currentTrigger.closest(SELECTORS.EXPAND_COLLAPSE_CONTAINER);
    if (!container) return;

    const allToggles = Array.from(container.querySelectorAll<HTMLElement>(SELECTORS.READ_TOGGLE));
    const isOpen = (toggle: HTMLElement) => getCurrentState(toggle) === STATES.LESS;

    allToggles.forEach((toggle) => {
      if (toggle !== currentTrigger && isOpen(toggle)) {
        toggle.click();
      }
    });
  }

  // ---------- CONTROL PANEL TEXT UPDATE ----------
  function updateControlPanelText(trigger: HTMLElement, dataSelector: string): void {
    const overrideMoreLess = trigger.hasAttribute(DATA_ATTRIBUTES.OVERRIDE_MORE_LESS);
    const hasPanelMore = trigger.hasAttribute(DATA_ATTRIBUTES.PANEL_MORE_ATTRIBUTE);
    const hasPanelLess = trigger.hasAttribute(DATA_ATTRIBUTES.PANEL_LESS_ATTRIBUTE);

    if (overrideMoreLess || !hasPanelMore || !hasPanelLess) return;

    const controlPanel = trigger.querySelector(SELECTORS.CONTROL_PANEL_CLASS);
    if (!controlPanel) return;

    const text = trigger.getAttribute(`data-${dataSelector}`) || '';
    controlPanel.textContent = text;
  }

  // ---------- STATE / VISIBILITY UTILS ----------
  function getTarget(trigger: HTMLElement, parent: HTMLElement): HTMLElement | null{
    let targetElement: HTMLElement | null = parent.querySelector<HTMLElement>(SELECTORS.READ_TARGET);

    if (!targetElement && !trigger.classList.contains(CLASSNAMES.SHOW_ALL)) {
      targetElement = parent.parentElement?.querySelector<HTMLElement>(SELECTORS.READ_TARGET) ?? null;
    }

    return targetElement;
  }

  function isNoJsModeActive(): boolean {
    return document.documentElement.classList.contains(SELECTORS.NO_JS_CLASS);
  }

  function updateTriggerState(trigger: HTMLElement, state: string): void {
    trigger.setAttribute(DATA_ATTRIBUTES.STATE, state);
  }

  function getCurrentState(trigger: HTMLElement): string {
    return trigger.getAttribute(DATA_ATTRIBUTES.STATE) || STATES.MORE;
  }

  function revealTarget(target: HTMLElement | undefined): void {
    if (!target) return;
    target.classList.remove(CLASSNAMES.COLLAPSE);
    target.classList.add(CLASSNAMES.SHOW);
  }

  function hideTarget(target: HTMLElement | undefined): void {
    if (!target) return;
    target.classList.remove(CLASSNAMES.SHOW);
    target.classList.add(CLASSNAMES.COLLAPSE);
  }

  function isTargetEffectivelyEmpty(target: HTMLElement | undefined): boolean {
    return !target?.textContent;
  }

  function markAsDead(trigger: HTMLElement): void {
    trigger.classList.add(CLASSNAMES.DEAD);
  }

  function isQuestion(trigger: HTMLElement): boolean {
    return trigger.classList.contains(CLASSNAMES.QUESTION);
  }

  function isDead(trigger: HTMLElement): boolean {
    return trigger.classList.contains(CLASSNAMES.DEAD);
  }

  // ---------- ENTRY & EXPORT ----------
  function initExpandCollapse(): void {
    const readToggleElements = document.querySelectorAll<HTMLElement>(SELECTORS.READ_TOGGLE);

    readToggleElements.forEach((trigger) => {
      const context = buildToggleContext(trigger);
      if (!context) return;

      applyInitialVisibility(context);
      setToggleBehavior(context);
    });
  }

  // Return the public API
  return {
    initExpandCollapse: initExpandCollapse
  };
}));